import React, { useState, createContext, useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Support from './pages/Support';
import DataDeletion from './pages/DataDeletion';
import { translations, LANGUAGES } from './translations';
import './App.css';

export const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

const SUPPORTED_LANGS = LANGUAGES.map(l => l.code);

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const pathParts = location.pathname.split('/').filter(Boolean);
  const firstPart = pathParts[0];

  const isLangPrefix = SUPPORTED_LANGS.includes(firstPart) && firstPart !== 'en';
  let lang = isLangPrefix ? firstPart : 'en';

  const [language, setLanguageState] = useState(lang);

  const changeLanguage = (newLang) => {
    setLanguageState(newLang);
    // Remove old prefix if any
    const currentPathWithoutLang = isLangPrefix
      ? '/' + pathParts.slice(1).join('/')
      : location.pathname;

    // Add new prefix if not English
    const newPrefix = newLang === 'en' ? '' : `/${newLang}`;
    const sanitizedPath = currentPathWithoutLang === '/' ? '' : currentPathWithoutLang;
    navigate(`${newPrefix}${sanitizedPath}` || '/');
  };

  useEffect(() => {
    localStorage.setItem('chefyz-lang', lang);
    document.documentElement.lang = lang;
    setLanguageState(lang);
  }, [lang]);

  // If URL has /en explicitly, we redirect to remove it for SEO
  // Must be after hooks!
  if (firstPart === 'en') {
    const newPath = '/' + pathParts.slice(1).join('/');
    return <Navigate to={newPath} replace />;
  }

  const t = translations[lang];

  // For SEO hreflang tags
  const currentPathWithoutLang = isLangPrefix
    ? '/' + pathParts.slice(1).join('/')
    : location.pathname;

  const linkPrefix = language === 'en' ? '' : `/${language}`;

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t, linkPrefix }}>
      <Helmet>
        <html lang={lang} />
        {SUPPORTED_LANGS.map(l => {
          const prefix = l === 'en' ? '' : `/${l}`;
          const sanitized = currentPathWithoutLang === '/' ? '' : currentPathWithoutLang;
          return (
            <link
              key={l}
              rel="alternate"
              hreflang={l}
              href={`https://chefyz.ai${prefix}${sanitized}`}
            />
          );
        })}
        <link
          rel="alternate"
          hreflang="x-default"
          href={`https://chefyz.ai${currentPathWithoutLang === '/' ? '' : currentPathWithoutLang}`}
        />
      </Helmet>
      <div className="app">
        <Header />
        <Routes>
          {/* Default (English) Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/support" element={<Support />} />
          <Route path="/data-deletion" element={<DataDeletion />} />

          {/* Prefixed Routes for other languages */}
          {SUPPORTED_LANGS.filter(l => l !== 'en').map(l => (
            <Route key={l} path={`/${l}`}>
              <Route index element={<Home />} />
              <Route path="privacy" element={<Privacy />} />
              <Route path="terms" element={<Terms />} />
              <Route path="support" element={<Support />} />
              <Route path="data-deletion" element={<DataDeletion />} />
            </Route>
          ))}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Footer />
      </div>
    </LanguageContext.Provider>
  );
}

export default App;
